import { useCopyAction } from "@repo/ui/clipboard/useCopyAction";
import { MarkdownContent } from "@repo/ui/markdown/markdownContent";
import { createTagPlugin, type TagHandler } from "@repo/ui/markdown/tagPlugin";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { toString as mdastToString } from "mdast-util-to-string";
import {
  type AnchorHTMLAttributes,
  type FC,
  type ReactNode,
  useMemo,
} from "react";

const HIGHLIGHT_URL_PROTOCOL = "alpharun-highlight:";

const tagHandlers: TagHandler[] = [
  {
    open: /^<highlight>$/,
    close: /^<\/highlight>$/,
    wrapPhrasingContent: (_match, children) => {
      const text = mdastToString({ type: "root", children });
      const params = new URLSearchParams({ text });
      return {
        type: "link",
        url: `${HIGHLIGHT_URL_PROTOCOL}//copy?${params.toString()}`,
        children,
      };
    },
  },
];

const HighlightLink: FC<AnchorHTMLAttributes<HTMLElement>> = (props) => {
  const parsedUrl = useMemo(() => {
    if (!props.href) {
      return undefined;
    }
    try {
      return new URL(props.href);
    } catch {
      return undefined;
    }
  }, [props.href]);

  if (parsedUrl?.protocol === HIGHLIGHT_URL_PROTOCOL) {
    const text = parsedUrl.searchParams.get("text") ?? "";
    return <Highlight text={text}>{props.children}</Highlight>;
  }

  return <a {...props} rel="noopener noreferrer" />;
};

const Highlight: FC<{ text: string; children: ReactNode }> = (props) => {
  const { didJustCopy, onCopy } = useCopyAction(props.text);

  return (
    <span
      onClick={onCopy}
      className="group/highlight relative inline-flex cursor-pointer items-center rounded-md bg-amber-100/70 px-1 font-medium text-amber-900 transition-colors hover:bg-amber-200/80"
    >
      {props.children}
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center rounded-r-md opacity-0 transition-opacity group-hover/highlight:pointer-events-auto group-hover/highlight:opacity-100">
        <span className="h-full w-6 bg-gradient-to-r from-transparent to-amber-200/90" />
        <span className="flex h-full items-center rounded-r-md bg-amber-200/90 pr-1">
          {didJustCopy ? (
            <IconCheck className="size-3 text-emerald-600" />
          ) : (
            <IconCopy className="size-3 text-amber-700" />
          )}
        </span>
      </span>
    </span>
  );
};

interface LiveCoachingMessageContentProps {
  content: string;
  isStreaming: boolean;
}

export const LiveCoachingMessageContent: FC<LiveCoachingMessageContentProps> = (
  props,
) => {
  const remarkPlugins = useMemo(
    () => [
      createTagPlugin({
        handlers: tagHandlers,
        trimsPartialTags: props.isStreaming,
      }),
    ],
    [props.isStreaming],
  );

  return (
    <MarkdownContent
      remarkPlugins={remarkPlugins}
      LinkComponent={HighlightLink}
    >
      {props.content}
    </MarkdownContent>
  );
};
